export const agreedToCookiesScriptConsent = useScriptTriggerConsent({
  postConsentTrigger: (resolve) => {
    if (!import.meta.env.DEV) {
      onNuxtReady(resolve)
    }
  }
})

export type OptionalCookieName = 'marketing-cookie' | 'ads-cookie'

type GoogleConsentState = {
  analytics_storage: 'granted' | 'denied'
  ad_storage: 'granted' | 'denied'
  ad_user_data: 'granted' | 'denied'
  ad_personalization: 'granted' | 'denied'
}

const allDenied: GoogleConsentState = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied'
}

function mapOptionalCookiesToConsentState(
  acceptedOptional: OptionalCookieName[]
): GoogleConsentState {
  const marketingTag = acceptedOptional.includes('marketing-cookie')
    ? 'granted'
    : 'denied'
  const adsTag = acceptedOptional.includes('ads-cookie') ? 'granted' : 'denied'

  return {
    analytics_storage: marketingTag,
    ad_storage: adsTag,
    ad_user_data: adsTag,
    ad_personalization: adsTag
  }
}

type GoogleTagManager = ReturnType<typeof useScriptGoogleTagManager>

export function getGoogleTagManager(): {
  consent?: GoogleTagManager['consent']
  remove: GoogleTagManager['remove']
  reload: GoogleTagManager['reload']
} {
  if (import.meta.env.DEV) {
    return {
      consent: { default: () => {}, update: () => {} },
      remove: () => {
        return true
      },
      reload: async () => ({}) as Awaited<ReturnType<GoogleTagManager['reload']>>
    }
  }

  return useScriptGoogleTagManager({
    id: 'GTM-W4NJ4RF',
    scriptOptions: {
      trigger: agreedToCookiesScriptConsent
    }
  })
}

// Once googleTagManager.remove() has run, the GTM <script> instance and its
// consent trigger promise are both spent. A same-session re-accept needs to
// call reload() to get a fresh script instance instead of relying on the
// (already-resolved) accept()/revoke() trigger.
let hasGtmBeenRemoved = false

export function grantConsent(
  googleTagManager: ReturnType<typeof getGoogleTagManager>,
  acceptedOptional: OptionalCookieName[]
) {
  if (hasGtmBeenRemoved) {
    googleTagManager.reload()
    hasGtmBeenRemoved = false
  }

  agreedToCookiesScriptConsent.accept()
  googleTagManager.consent?.update(mapOptionalCookiesToConsentState(acceptedOptional))
}

export function revokeConsent(googleTagManager: ReturnType<typeof getGoogleTagManager>) {
  googleTagManager.consent?.update(allDenied)
  agreedToCookiesScriptConsent.revoke()
  googleTagManager.remove()
  hasGtmBeenRemoved = true
}

export function applyStoredConsentOnLoad(
  googleTagManager: ReturnType<typeof getGoogleTagManager>,
  isConsentGiven: boolean | undefined,
  cookiesEnabledIds: string[] | undefined
) {
  if (isConsentGiven !== true) {
    return
  }

  const acceptedOptional = (cookiesEnabledIds ?? []).filter(
    (id): id is OptionalCookieName => id === 'marketing-cookie' || id === 'ads-cookie'
  )

  if (acceptedOptional.length === 0) {
    return
  }

  grantConsent(googleTagManager, acceptedOptional)
}
